import { Component, EventEmitter, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ChartLabelInfo } from '@models/ChartLabel/chartLabelInfo.model';
import { ChartLabelProperties } from '@models/ChartLabel/chartLabelProperties.model';

// Floating editor for a chart favorite's labels. Opened (non-blocking, so the
// user can drag the label handles on the chart while it is open) by the eChart
// component's "Labels" button. It is a property editor + list only — all
// persistence and on-chart geometry live in the eChart component, which owns the
// `labels` references this modal mutates and listens to the outputs below.
@Component({
  standalone: false,
  selector: 'chart-label-editor-modal',
  templateUrl: './chart-label-editor-modal.component.html',
  styleUrls: ['./chart-label-editor-modal.component.scss']
})
export class ChartLabelEditorModalComponent {
  // Live list of labels (the same references the eChart component holds). Pushed
  // in by the eChart component on open and after each change.
  labels: ChartLabelInfo[] = [];

  // Label type used when "Add" is clicked.
  newLabelType = 1;

  labelTypes = [
    { value: 1, label: 'Text + arrow' },
    { value: 2, label: 'Sticky note with text + arrow' },
    { value: 4, label: 'Text Box, no arrow' },
    { value: 3, label: 'Vertical Band' },
    { value: 5, label: 'Horizontal Band' }
  ];

  // The label currently being edited (a reference into `labels`).
  selected: ChartLabelInfo | null = null;

  // Working style fields for the selected label (parsed from its properties bag).
  props: ChartLabelProperties = {};

  modalDragOn = false;

  @Output() onAdd = new EventEmitter<number>();            // emits the new labelType
  @Output() onUpdate = new EventEmitter<ChartLabelInfo>(); // emits the edited label
  @Output() onDelete = new EventEmitter<number>();         // emits the label id
  @Output() onClosed = new EventEmitter<void>();

  constructor(public bsModalRef: BsModalRef) {}

  setDragOn(on: boolean): void {
    this.modalDragOn = on;
  }

  add(): void {
    this.onAdd.emit(Number(this.newLabelType));
  }

  select(label: ChartLabelInfo): void {
    this.selected = label;
    this.props = this.parseProps(label);
  }

  // After the eChart component persists an added label it pushes the refreshed
  // list and calls this so the new label is selected for immediate editing.
  selectById(id: number): void {
    const found = this.labels.find(l => l.id === id);
    if (found != undefined) {
      this.select(found);
    }
  }

  isSelected(label: ChartLabelInfo): boolean {
    return this.selected != undefined && this.selected.id === label.id;
  }

  // Push the edited text / type / style back onto the selected label and notify
  // the eChart component to persist + re-render.
  applyChanges(): void {
    if (this.selected == undefined) {
      return;
    }
    this.selected.properties = JSON.stringify(this.props ?? {});
    this.onUpdate.emit(this.selected);
  }

  delete(label: ChartLabelInfo): void {
    this.onDelete.emit(label.id);
    if (this.selected != undefined && this.selected.id === label.id) {
      this.selected = null;
    }
  }

  close(): void {
    this.onClosed.emit();
    this.bsModalRef.hide();
  }

  // A band (vertical = 3, horizontal = 5): color-only, no text/arrow.
  get isBandType(): boolean {
    return this.selected != undefined && (this.selected.labelType === 3 || this.selected.labelType === 5);
  }

  // Whether the arrow toggle applies (only the arrow text labels, Types 1 & 2).
  get hasArrow(): boolean {
    return this.selected != undefined && (this.selected.labelType === 1 || this.selected.labelType === 2);
  }

  labelPreview(label: ChartLabelInfo): string {
    const t = (label.text ?? '').trim();
    // Bands carry no text, so show their type name in the list instead.
    return t.length > 0 ? t : this.typeName(label.labelType);
  }

  typeName(labelType: number): string {
    const found = this.labelTypes.find(t => t.value === labelType);
    return found != undefined ? found.label : ('Type ' + labelType);
  }

  private parseProps(label: ChartLabelInfo): ChartLabelProperties {
    let p: ChartLabelProperties = {};
    if (label.properties != undefined && label.properties !== '') {
      try {
        p = JSON.parse(label.properties);
      } catch {
        p = {};
      }
    }
    return {
      fontSize: p.fontSize ?? 12,
      fontColor: p.fontColor ?? '#222222',
      hasStickyBackground: p.hasStickyBackground ?? (label.labelType === 2),
      hasShadow: p.hasShadow ?? (label.labelType === 2 || label.labelType === 4),
      bandColor: p.bandColor ?? '#91cc75',
      showArrow: p.showArrow ?? (label.labelType !== 4)
    };
  }
}
